import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, parseISO } from 'date-fns';
import { fr, enGB } from 'date-fns/locale';
import i18n from '@/i18n';
import type { Maintenance } from '@/hooks/useMaintenances';

interface ExportOptions {
  maintenances: Maintenance[];
  annee: number;
  bateauNom?: string;
}

const getDateLocale = () => (i18n.language?.startsWith('en') ? enGB : fr);

export const exportMaintenancesPDF = ({ maintenances, annee, bateauNom }: ExportOptions) => {
  const t = i18n.getFixedT(i18n.language, 'maintenance');
  const dateLocale = getDateLocale();
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header band
  doc.setFillColor(20, 26, 51); // nautic navy
  doc.rect(0, 0, pageWidth, 28, 'F');

  doc.setTextColor(234, 179, 8); // gold
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(t('pdf.brand'), 14, 13);

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(t('pdf.title', { year: annee }), 14, 22);

  // Subheader
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  let y = 36;
  if (bateauNom) {
    doc.text(t('pdf.boat', { name: bateauNom }), 14, y);
    y += 5;
  }
  doc.text(t('pdf.generatedOn', { date: format(new Date(), 'dd/MM/yyyy', { locale: dateLocale }) }), 14, y);
  y += 4;

  // Filter completed maintenances for the year, sorted by date
  const sorted = [...maintenances].sort(
    (a, b) => new Date(a.date_echeance).getTime() - new Date(b.date_echeance).getTime()
  );

  const totalCout = sorted.reduce((sum, m) => sum + (m.prix || 0), 0);

  // Table
  autoTable(doc, {
    startY: y + 4,
    head: [[
      t('pdf.columns.date'),
      t('pdf.columns.title'),
      t('pdf.columns.category'),
      t('pdf.columns.description'),
      t('pdf.columns.cost'),
    ]],
    body: sorted.map((m) => [
      format(parseISO(m.date_echeance), 'dd/MM/yyyy', { locale: dateLocale }),
      m.titre,
      m.categorie || t('pdf.notAvailable'),
      m.description || t('pdf.notAvailable'),
      m.prix != null ? m.prix.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) : t('pdf.notAvailable'),
    ]),
    foot: [['', '', '', t('pdf.total'), totalCout.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €']],
    headStyles: { fillColor: [20, 26, 51], textColor: [234, 179, 8], fontStyle: 'bold' },
    footStyles: { fillColor: [240, 240, 245], textColor: [20, 26, 51], fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 24 },
      1: { cellWidth: 40 },
      2: { cellWidth: 30 },
      3: { cellWidth: 'auto' },
      4: { cellWidth: 25, halign: 'right' },
    },
    margin: { left: 14, right: 14 },
    didDrawPage: () => {
      const pageCount = doc.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        t('pdf.footer', { page: doc.getCurrentPageInfo().pageNumber, pages: pageCount }),
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
    },
  });

  if (sorted.length === 0) {
    const finalY = (doc as any).lastAutoTable?.finalY || y + 10;
    doc.setFontSize(11);
    doc.setTextColor(120, 120, 120);
    doc.text(t('pdf.noMaintenance'), 14, finalY + 10);
  }

  doc.save(`maintenances-${annee}${bateauNom ? '-' + bateauNom.replace(/\s+/g, '_') : ''}.pdf`);
};
